// org-parking-edit/org-parking-edit.component.ts
import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DiscountParkingQuota, DynamicPricingRule, DynamicPricingRuleType, ParkingVM, SpecialPricingPeriod } from '../../../../../models/parkingsite.model';
import { OrganizationDiscountCheckVM } from '../../../../../models/orderModel';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import moment from 'moment';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-org-parking-edit',
  templateUrl: './org-parking-edit.component.html',
  styleUrls: ['./org-parking-edit.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OrgParkingEditComponent implements OnInit {
  parkingForm: FormGroup;
  isEditMode = false;
  isLoading = false;
  @Output() saveChangesEvent = new EventEmitter<ParkingVM>();
  readonly dynamicPricingRuleTypes: DynamicPricingRuleType[] = ['OccupiedPercentAbove', 'OccupiedPercentBelow', 'EmptySpacesAbove', 'EmptySpacesBelow'];
  discountChecks: OrganizationDiscountCheckVM[] = [];
  quotaSaveError: string | null = null;
  quotaLoadError: string | null = null;
  private initialDiscountQuotas = '';

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<OrgParkingEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { parking: ParkingVM },
    private backOfficeService: BackOfficeService,
    private snackBar: MatSnackBar
  ) {
    this.initializeParkingEditForm();
  }

  ngOnInit(): void {
    if (this.data?.parking) {
      this.isEditMode = true;
      this.populateForm(this.data.parking);
      this.loadDiscountQuotas(this.data.parking);
    }
  }

  get discountQuotas(): FormArray<FormGroup> {
    return this.parkingForm.get('discountQuotas') as FormArray<FormGroup>;
  }

  private loadDiscountQuotas(parking: ParkingVM): void {
    forkJoin({
      discountChecks: this.backOfficeService.getOrganizationDiscountChecks(parking.organizationId),
      quotas: this.backOfficeService.getParkingDiscountQuotas(parking.organizationId, parking.parkingId)
    }).subscribe({
      error: (error: any) => {
        // Quotas stay unknown: they are not saved (initialDiscountQuotas stays empty), show why
        console.error('Error loading discount quotas:', error);
        this.quotaLoadError = typeof error?.error === 'string' && error.error ? error.error : `${error?.status ?? ''} ${error?.statusText ?? ''}`.trim();
      },
      next: ({ discountChecks, quotas }) => {
        this.quotaLoadError = null;
        // Codes valid for this parking (no parking = all parkings); keep the codes of saved quotas even if they are bound elsewhere
        this.discountChecks = (discountChecks ?? [])
          .filter(d => !d.parkingId || d.parkingId === parking.parkingId || (quotas ?? []).some(q => q.discountCheckId === d.id))
          .sort((a, b) => (a.discountCode ?? '').localeCompare(b.discountCode ?? ''));

        this.discountQuotas.clear();
        (quotas ?? []).forEach(quota => this.addDiscountQuota(quota));
        this.initialDiscountQuotas = JSON.stringify(this.getDiscountQuotasValue());
      }
    });
  }

  addDiscountQuota(quota?: DiscountParkingQuota): void {
    this.discountQuotas.push(this.fb.group({
      id: [quota?.id ?? null],
      discountCheckId: [quota?.discountCheckId ?? null, Validators.required],
      minPercent: [quota?.minPercent ?? null, [Validators.min(0), Validators.max(100)]],
      maxPercent: [quota?.maxPercent ?? null, [Validators.min(0), Validators.max(100)]],
      from: [quota?.from ? OrgParkingEditComponent.parseDateOnly(quota.from) : null],
      to: [quota?.to ? OrgParkingEditComponent.parseDateOnly(quota.to) : null]
    }, { validators: this.discountQuotaValidator }));
  }

  removeDiscountQuota(index: number): void {
    this.discountQuotas.removeAt(index);
  }

  private getDiscountQuotasValue(): DiscountParkingQuota[] {
    const percent = (value: any) => value === null || value === '' || value === undefined ? null : Number(value);
    const date = (value: any) => value ? OrgParkingEditComponent.formatDateOnly(value) : null;
    return this.discountQuotas.value.map((q: any) => ({
      id: q.id,
      discountCheckId: q.discountCheckId,
      minPercent: percent(q.minPercent),
      maxPercent: percent(q.maxPercent),
      from: date(q.from),
      to: date(q.to)
    }));
  }

  // Same rules as DiscountParkingQuotaManager.Validate on backend; needs the loaded discount codes
  private discountQuotaValidator = (group: AbstractControl): ValidationErrors | null => {
    const { discountCheckId, minPercent, maxPercent, from, to } = group.value;
    const has = (value: any) => value !== null && value !== '' && value !== undefined;
    const day = (value: any) => moment(value).format('YYYY-MM-DD');

    if (!has(minPercent) && !has(maxPercent)) return { noLimits: true };
    if (has(minPercent) && has(maxPercent) && Number(minPercent) > Number(maxPercent)) return { minAboveMax: true };
    if (from && to && day(to) < day(from)) return { endsBeforeStart: true };

    // The discount must be valid during the whole quota period, an open quota period needs an open discount
    const discountCheck = this.discountChecks.find(d => d.id === discountCheckId);
    if (discountCheck?.validFrom && (!from || day(from) < day(discountCheck.validFrom))) return { startsBeforeDiscount: true };
    if (discountCheck?.validTo && (!to || day(to) > day(discountCheck.validTo))) return { endsAfterDiscount: true };
    return null;
  };

  // Quotas of a parking may not overlap; empty from/to = open period
  private static discountQuotasOverlapValidator(array: AbstractControl): ValidationErrors | null {
    const day = (value: any) => value ? moment(value).format('YYYY-MM-DD') : null;
    const periods = (array.value as any[])
      .map(q => ({ from: day(q.from), to: day(q.to) }))
      .sort((a, b) => (a.from ?? '').localeCompare(b.from ?? ''));

    for (let i = 1; i < periods.length; i++) {
      const [previous, next] = [periods[i - 1], periods[i]];
      if (!previous.to || !next.from || previous.to >= next.from) {
        return { overlap: { a: `${previous.from ?? '…'} – ${previous.to ?? '…'}`, b: `${next.from ?? '…'} – ${next.to ?? '…'}` } };
      }
    }
    return null;
  }

  initializeParkingEditForm() {
    this.parkingForm = this.fb.group({
      parkingName: ['', Validators.required],
      parkingDescription: this.fb.group({
        en: [''],
        sv: ['']
      }),
      parkingMarginHours: [null],
      communicationParkingSpotLevel: [null],
      freeParkingSiteGapHours: [null],
      email: [''],
      emailName: [''],
      confirmationTemplate: [''],
      additionalPaymentTemplate: [''],
      checkinTemplate: [''],
      welcomeHomeTemplate: [''],
      additionalServiceOfferTemplate: [''],
      cancelTemplate: [''],
      active: [false],
      additionalServices: [false],
      disabledParkingSpace: [false],
      indoors: [false],
      largeCars: [false],
      electricalCharging: [false],
      subscriptionParking: [false],
      overbooking: [false],
      allowMultipleEntries: [false],
      allowDriveUp: [false],
      hideOnCustomerPortal: [false],
      allowDynamicPricing: [false],
      dynamicPricingRules: this.fb.array([]),
      specialPricingPeriods: this.fb.array([], { validators: OrgParkingEditComponent.specialPricingPeriodsOverlapValidator }),
      checkinBeforeParkingStart: [0],
      checkinAfterParkingStart: [0],
      checkOutBeforeParkingEnd: [0],
      checkOutAfterParkingEnd: [0],
      workFrom: [null],
      workTo: [null],
      noShowStatusAfterHours: [null],
      discountQuotas: this.fb.array([], { validators: OrgParkingEditComponent.discountQuotasOverlapValidator }),
      feature1: this.fb.group({ en: [''], sv: [''] }),
      feature2: this.fb.group({ en: [''], sv: [''] }),
      feature3: this.fb.group({ en: [''], sv: [''] }),
      feature4: this.fb.group({ en: [''], sv: [''] }),
      feature5: this.fb.group({ en: [''], sv: [''] }),
      feature6: this.fb.group({ en: [''], sv: [''] })
    });
  }

  private populateForm(parking: ParkingVM): void {
    const formValues: any = {
      parkingName: parking.parkingName,
      parkingDescription: this.parseMultilingualField(parking.parkingDescription),
      parkingMarginHours: parking.parkingMarginHours,
      communicationParkingSpotLevel: parking.communicationParkingSpotLevel,
      freeParkingSiteGapHours: parking.freeParkingSiteGapHours,
      email: parking.email,
      emailName: parking.emailName,
      confirmationTemplate: parking.confirmationTemplate,
      additionalPaymentTemplate: parking.additionalPaymentTemplate,
      checkinTemplate: parking.checkinTemplate,
      welcomeHomeTemplate: parking.welcomeHomeTemplate,
      additionalServiceOfferTemplate: parking.additionalServiceOfferTemplate,
      cancelTemplate: parking.cancelTemplate,
      active: parking.active,
      additionalServices: parking.additionalServices,
      disabledParkingSpace: parking.disabledParkingSpace,
      indoors: parking.indoors,
      largeCars: parking.largeCars,
      electricalCharging: parking.electricalCharging,
      subscriptionParking: parking.subscriptionParking,
      overbooking: parking.overbooking,
      allowMultipleEntries: parking.allowMultipleEntries,
      allowDriveUp: parking.allowDriveUp ?? false,
      hideOnCustomerPortal: parking.hideOnCustomerPortal,
      allowDynamicPricing: parking.allowDynamicPricing ?? false,
      checkinBeforeParkingStart: parking.checkinBeforeParkingStart || 0,
      checkinAfterParkingStart: parking.checkinAfterParkingStart || 0,
      checkOutBeforeParkingEnd: parking.checkOutBeforeParkingEnd || 0,
      checkOutAfterParkingEnd: parking.checkOutAfterParkingEnd || 0,
      workFrom: parking.workFrom || null,
      workTo: parking.workTo || null,
      noShowStatusAfterHours: parking.noShowStatusAfterHours ?? null
    };

    // Handle features
    for (let i = 1; i <= 6; i++) {
      const featureKey = `feature${i}`;
      const featureValue = parking[featureKey as keyof ParkingVM];
      formValues[featureKey] = this.parseMultilingualField(featureValue);
    }

    this.parkingForm.patchValue(formValues);

    this.dynamicPricingRules.clear();
    (parking.dynamicPricingRules ?? []).forEach(rule => this.addDynamicPricingRule(rule));

    this.specialPricingPeriods.clear();
    (parking.specialPricingPeriods ?? []).forEach(period => this.addSpecialPricingPeriod(period));
  }

  get specialPricingPeriods(): FormArray<FormGroup> {
    return this.parkingForm.get('specialPricingPeriods') as FormArray<FormGroup>;
  }

  addSpecialPricingPeriod(period?: SpecialPricingPeriod): void {
    this.specialPricingPeriods.push(this.fb.group({
      name: [period?.name ?? '', Validators.required],
      from: [period ? OrgParkingEditComponent.parseDateOnly(period.from) : null, Validators.required],
      to: [period ? OrgParkingEditComponent.parseDateOnly(period.to) : null, Validators.required],
      recurring: [period?.recurring ?? true],
      multiplier: [period?.multiplier ?? null, [Validators.required, Validators.min(0.01)]],
    }, { validators: OrgParkingEditComponent.specialPricingPeriodValidator }));
  }

  removeSpecialPricingPeriod(index: number): void {
    this.specialPricingPeriods.removeAt(index);
  }

  private static toSpecialPricingPeriod(row: any): SpecialPricingPeriod {
    return { ...row, from: OrgParkingEditComponent.formatDateOnly(row.from), to: OrgParkingEditComponent.formatDateOnly(row.to) };
  }

  // DateOnly <-> datepicker value (moment via the shared DateAdapter) without timezone shifts
  private static parseDateOnly(value: string): moment.Moment {
    return moment(value, 'YYYY-MM-DD');
  }

  private static formatDateOnly(value: moment.MomentInput): string {
    return moment(value).format('YYYY-MM-DD');
  }

  private static specialPricingPeriodValidator(group: AbstractControl): ValidationErrors | null {
    const { from, to, recurring } = group.value;
    return from && to && !recurring && moment(to).isBefore(moment(from), 'day') ? { endsBeforeStart: true } : null;
  }

  // Same rules as SpecialPricingPeriod.Overlaps on backend: recurring periods are expanded over a few years
  private static specialPricingPeriodsOverlapValidator(array: AbstractControl): ValidationErrors | null {
    const periods = (array.value as any[])
      .filter(p => p.from && p.to)
      .map(p => ({ ...p, from: moment(p.from).startOf('day').toDate(), to: moment(p.to).startOf('day').toDate() }));
    const years = periods.flatMap(p => [p.from.getFullYear(), p.to.getFullYear()]).concat([2027, 2028, 2029]);
    const [minYear, maxYear] = [Math.min(...years) - 1, Math.max(...years) + 1];

    const occurrences = (p: any): [Date, Date][] => {
      if (!p.recurring) return [[p.from, p.to]];
      const inYear = (d: Date, y: number) => new Date(y, d.getMonth(), Math.min(d.getDate(), new Date(y, d.getMonth() + 1, 0).getDate()));
      const crossesNewYear = p.to.getMonth() * 100 + p.to.getDate() < p.from.getMonth() * 100 + p.from.getDate();
      const result: [Date, Date][] = [];
      for (let y = minYear; y <= maxYear; y++) result.push([inYear(p.from, y), inYear(p.to, crossesNewYear ? y + 1 : y)]);
      return result;
    };

    for (let i = 0; i < periods.length; i++) {
      for (let j = i + 1; j < periods.length; j++) {
        const overlap = occurrences(periods[i]).some(([aFrom, aTo]) => occurrences(periods[j]).some(([bFrom, bTo]) => aFrom <= bTo && bFrom <= aTo));
        if (overlap) return { overlap: { a: periods[i].name, b: periods[j].name } };
      }
    }
    return null;
  }

  get dynamicPricingRules(): FormArray<FormGroup> {
    return this.parkingForm.get('dynamicPricingRules') as FormArray<FormGroup>;
  }

  addDynamicPricingRule(rule?: DynamicPricingRule): void {
    this.dynamicPricingRules.push(this.fb.group({
      type: [rule?.type ?? 'OccupiedPercentAbove', Validators.required],
      value: [rule?.value ?? null, [Validators.required, Validators.min(0)]],
      multiplier: [rule?.multiplier ?? null, [Validators.required, Validators.min(0.01)]],
    }, { validators: OrgParkingEditComponent.dynamicPricingRuleValidator }));
  }

  removeDynamicPricingRule(index: number): void {
    this.dynamicPricingRules.removeAt(index);
  }

  isPercentRule(type: DynamicPricingRuleType): boolean {
    return type.startsWith('OccupiedPercent');
  }

  private static dynamicPricingRuleValidator(group: AbstractControl): ValidationErrors | null {
    const { type, value } = group.value as DynamicPricingRule;
    if (type.startsWith('OccupiedPercent') && value > 100) return { percentOutOfRange: true };
    if (type.startsWith('EmptySpaces') && value != null && !Number.isInteger(value)) return { notWholeNumber: true };
    return null;
  }

  private parseMultilingualField(value: any): { en: string, sv: string } {
    if (!value) return { en: '', sv: '' };

    try {
      const parsed = JSON.parse(value as string);
      return {
        en: parsed.en || '',
        sv: parsed.sv || ''
      };
    } catch {
      return { en: value, sv: '' };
    }
  }

  private stringifyMultilingualField(value: { en: string, sv: string }): string {
    return JSON.stringify(value);
  }

  private ifTranslatorThingyEmpty(value: { en: string, sv: string }) {
    if ((!value && !value.en && !value.sv) || ( value.en==="" && value.sv==="" ) ) return null;
    return value;
  }

  clearWorkPeriod(): void {
    this.parkingForm.patchValue({ workFrom: null, workTo: null });
  }

  onSubmit(): void {
    if (this.parkingForm.valid) {
      const formValue = this.parkingForm.value;

      // Convert feature objects to JSON strings
      // const features: {[key: string]: string} = {};
      // for (let i = 1; i <= 6; i++) {
      //   const featureKey = `feature${i}`;
      //   const featureValue = formValue[featureKey];
      //   if (featureValue && (featureValue.en || featureValue.sv)) {
      //     features[featureKey] = JSON.stringify({
      //       en: featureValue.en || '',
      //       sv: featureValue.sv || ''
      //     });
      //   } else {
      //     features[featureKey] = '';
      //   }
      // }

      const parking: ParkingVM = {
        parkingId: this.data?.parking?.parkingId || '',
        organizationId: this.data?.parking?.organizationId || '',
        parkingName: formValue.parkingName,
        // parkingDescription: formValue.parkingDescription,
        parkingMarginHours: formValue.parkingMarginHours,
        communicationParkingSpotLevel: formValue.communicationParkingSpotLevel,
        freeParkingSiteGapHours: formValue.freeParkingSiteGapHours,
        email: formValue.email,
        emailName: formValue.emailName,
        confirmationTemplate: formValue.confirmationTemplate,
        additionalPaymentTemplate: formValue.additionalPaymentTemplate,
        checkinTemplate: formValue.checkinTemplate,
        welcomeHomeTemplate: formValue.welcomeHomeTemplate,
        additionalServiceOfferTemplate: formValue.additionalServiceOfferTemplate,
        cancelTemplate: formValue.cancelTemplate,
        active: formValue.active,
        additionalServices: formValue.additionalServices,
        disabledParkingSpace: formValue.disabledParkingSpace,
        indoors: formValue.indoors,
        largeCars: formValue.largeCars,
        electricalCharging: formValue.electricalCharging,
        subscriptionParking: formValue.subscriptionParking,
        overbooking: formValue.overbooking,
        allowMultipleEntries: formValue.allowMultipleEntries,
        allowDriveUp: formValue.allowDriveUp,
        hideOnCustomerPortal: formValue.hideOnCustomerPortal,
        allowDynamicPricing: formValue.allowDynamicPricing,
        dynamicPricingRules: formValue.dynamicPricingRules,
        specialPricingPeriods: formValue.specialPricingPeriods.map(OrgParkingEditComponent.toSpecialPricingPeriod),
        checkinBeforeParkingStart: formValue.checkinBeforeParkingStart,
        checkinAfterParkingStart: formValue.checkinAfterParkingStart,
        checkOutBeforeParkingEnd: formValue.checkOutBeforeParkingEnd,
        checkOutAfterParkingEnd: formValue.checkOutAfterParkingEnd,
        workFrom: formValue.workFrom || null,
        workTo: formValue.workTo || null,
        noShowStatusAfterHours: formValue.noShowStatusAfterHours ?? null,
        noShowStatusAfterHoursSet: true,
        parkingDescription: this.ifTranslatorThingyEmpty(this.parkingForm.value.parkingDescription),
        feature1: this.ifTranslatorThingyEmpty(this.parkingForm.value.feature1),
        feature2: this.ifTranslatorThingyEmpty(this.parkingForm.value.feature2),
        feature3: this.ifTranslatorThingyEmpty(this.parkingForm.value.feature3),
        feature4: this.ifTranslatorThingyEmpty(this.parkingForm.value.feature4),
        feature5: this.ifTranslatorThingyEmpty(this.parkingForm.value.feature5),
        feature6: this.ifTranslatorThingyEmpty(this.parkingForm.value.feature6)
        // feature1: features.feature1,
        // feature2: features.feature2,
        // feature3: features.feature3,
        // feature4: features.feature4,
        // feature5: features.feature5,
        // feature6: features.feature6
      };

      this.isLoading = true;
      this.quotaSaveError = null;
      this.backOfficeService.updateParkingInfo(parking).subscribe({
        next: (updatedParking: ParkingVM) => {
          const close = () => {
            this.isLoading = false;
            this.saveChangesEvent.emit(updatedParking);
            this.dialogRef.close(updatedParking);
          };

          const quotas = this.getDiscountQuotasValue();
          // Not loaded (new parking or load failed) or unchanged: nothing to save
          if (!this.initialDiscountQuotas || JSON.stringify(quotas) === this.initialDiscountQuotas) {
            close();
            return;
          }

          this.backOfficeService.saveParkingDiscountQuotas(parking.organizationId, parking.parkingId, quotas).subscribe({
            next: () => close(),
            error: (error: any) => {
              // The parking itself is saved, keep the dialog open so the quotas can be fixed
              console.error('Error saving discount quotas:', error);
              this.isLoading = false;
              this.quotaSaveError = typeof error?.error === 'string' ? error.error : '';
            }
          });
        },
        error: (error: any) => {
          console.error('Error updating parking:', error);
          if (error.status === 400 && typeof error.error === 'string') {
            // Validation error: keep the dialog open so the user can fix the form
            this.isLoading = false;
            this.snackBar.open(error.error, 'OK', { verticalPosition: 'top' });
            return;
          }
          this.dialogRef.close();
        }
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
